import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import type Voters from '../models/voters.model'
import type Voter from '../models/voter.model'
import type {
  Constituency,
  ConstituencyRegisteredVoter,
  PollingStationRegisteredVote,
  VoterCount
} from '../models/registered-voters'

import RegisteredVoterApiService from '../services/registered-voter-api.service'

import { trimAndLowercase } from '@/modules/core/utils/string.util'
import { useRegisteredVoterMapStore } from '@/modules/map/stores/registered-voter-map.store'
import { useVoronoiConstituencyMapStore } from '@/modules/map/stores/voronoi-constituency-map.store'
import { useConstituencyMapStore } from '@/modules/constituency/stores/constituency-map.store'
import type ConstituencyVoterModel from '@/modules/voter/models/constituency-voter.model'
import type ConstituencyVotersModel from '@/modules/voter/models/constituency-voters.model'
import { useParliamentaryMapStore } from '@/modules/map/stores/parliamentary-map.store'

const voterService = new RegisteredVoterApiService()

interface VotersAgeGroup {
  '18_30': number
  '31_40': number
  '41_50': number
  '51_60': number
  '61_100': number
  '101plus': number
}

interface StationData {
  region: string
  constituency: string
  station_code: string
  station_name: string
  total_voters: number
  total_voters_male: number
  total_voters_female: number
  voters: VotersAgeGroup
  voters_male: VotersAgeGroup
  voters_female: VotersAgeGroup
}

interface AggregatedData {
  totalStations: number
  totalVoters: number
  totalVotersMale: number
  totalVotersFemale: number
  voters: VotersAgeGroup
  votersMale: VotersAgeGroup
  votersFemale: VotersAgeGroup
  filteredStations: StationData[] // Added filtered stations to the result
}

export const useConstituencyVoterStore = defineStore('constituencyVoter', () => {
  const mIsLoading = ref<boolean>(false)

  const mVoterMapStore = useRegisteredVoterMapStore()
  const mConstituencyMapStore = useConstituencyMapStore()
  const mParliamentaryMapStore = useParliamentaryMapStore()

  // const mVoter = ref<Voter[]>([])
  const mSelectedVoter = ref<ConstituencyVoterModel | null>(null)
  const mVoterCounts = ref<ConstituencyRegisteredVoter[]>([])

  const mConstituencyRegisteredVoter = ref<ConstituencyRegisteredVoter[]>([])

  const mAgeGroups = ref<string[]>([
    '18_30',
    '31_40',
    '31_40',
    '41_50',
    '51_60',
    '61_100',
    '101plus'
  ])

  function resetStore() {
    // mSelectedVoter.value = mVoter.value[0] as Voter
  }

  const formatAgeGroup = (ageGroup: string): string => {
    if (ageGroup === '101plus') return '100+'
    return ageGroup.replace('_', '-')
  }

  function filterVotersBy(key: 'region' | 'constituency' | 'stationCode', value: string) {
    if (!mSelectedVoter.value) return []
    return mSelectedVoter.value.voters
      .filter((voter) => voter[key] !== undefined)
      .filter((voter) => voter[key].trim().toLowerCase() === value.trim().toLowerCase())
  }

  function aggregateAgeGroup(filteredVoters: ConstituencyVotersModel[], key: string) {
    return filteredVoters.reduce(
      (acc: any, curr: any) => {
        Object.keys(acc).forEach((ageGroup) => {
          acc[ageGroup] += curr[key][ageGroup] || 0
        })
        return acc
      },
      {
        '18_30': 0,
        '31_40': 0,
        '41_50': 0,
        '51_60': 0,
        '61_100': 0,
        '101plus': 0
      }
    )
  }

  function calculateTotals(filteredVoters: ConstituencyVotersModel[]) {
    const totalVoters = filteredVoters.reduce((acc, curr) => acc + curr.totalVoters, 0)
    const totalVotersMale = filteredVoters.reduce((acc, curr) => acc + curr.totalVotersMale, 0)
    const totalVotersFemale = filteredVoters.reduce((acc, curr) => acc + curr.totalVotersFemale, 0)

    return { totalVoters, totalVotersMale, totalVotersFemale }
  }

  function getVotersData(filteredVoters: ConstituencyVotersModel[]) {
    const { totalVoters, totalVotersMale, totalVotersFemale } = calculateTotals(filteredVoters)

    const votersAgeGroup = aggregateAgeGroup(filteredVoters, 'voters')
    const votersMaleAgeGroup = aggregateAgeGroup(filteredVoters, 'votersMale')
    const votersFemaleAgeGroup = aggregateAgeGroup(filteredVoters, 'votersFemale')

    return {
      region: '',
      constituency: '',
      totalVoters: totalVoters,
      totalVotersMale: totalVotersMale,
      totalVotersFemale: totalVotersFemale,
      voters: votersAgeGroup,
      votersMale: votersMaleAgeGroup,
      votersFemale: votersFemaleAgeGroup
    } as Voters
  }

  function getVotersRegion(region: string) {
    const filteredVoters = filterVotersBy('region', region)
    return getVotersData(filteredVoters)
  }

  function getVotersConstituency(constituency: string) {
    const filteredVoters = filterVotersBy('constituency', constituency)
    return getVotersData(filteredVoters)
  }

  function getVotersPollingStation(stationCode: string) {
    const filteredVoters = filterVotersBy('stationCode', stationCode)
    return getVotersData(filteredVoters)
  }

  function getTotalVotersCount(data: VoterCount[], year: 2020 | 2024): number {
    return data.reduce((total, region) => total + region[`voter_count_${year}`], 0)
  }

  function getTotalRatio(data: VoterCount[], year: 2020 | 2024): number {
    return data.reduce((total, region) => total + region[`ratio_${year}`], 0)
  }

  function calculateNetGrowth(
    constituencies: PollingStationRegisteredVote[] | ConstituencyRegisteredVoter[]
  ): number {
    return constituencies.reduce((total, c) => total + (c.voter_count_2024 - c.voter_count_2020), 0)
  }

  function calculatePercentageGrowth(constituencies: Constituency): number {
    const totalVoters2020 = constituencies.voter_count_2020 ?? 0
    const totalVoters2024 = constituencies.voter_count_2024 ?? 0
    return ((totalVoters2024 - totalVoters2024) / totalVoters2020) * 100
  }

  function getRegisteredVotersPollingStation(stationCode: string) {
    const filteredResults =
      mVoterCounts.value
        .map((constituency) => ({
          ...constituency,
          polling_stations: constituency.polling_stations.filter(
            (c) => trimAndLowercase(c.code) === trimAndLowercase(stationCode)
          )
        }))
        .filter((constituency) => constituency.polling_stations.length > 0) || []

    if (!filteredResults || filteredResults.length < 1) return 0

    const allPollingStations = filteredResults.flatMap(
      (constituency) => constituency.polling_stations
    )

    return {
      net: calculateNetGrowth(allPollingStations),
      percentage: allPollingStations[0].percentage
      // ratio: ((year === 2020 ? allPollingStations[0].ratio_2020 : allPollingStations[0].ratio_2024))
    }
  }

  //
  // function getRegisteredVotersPollingStation(pollingStation: string) {
  //   const filteredResults =
  //     mConstituencyRegisteredVoter.value
  //       .map((constituency) => ({
  //         ...constituency,
  //         polling_stations: constituency.polling_stations.filter(
  //           (s) => trimAndLowercase(s.code) === trimAndLowercase(pollingStation)
  //         )
  //       }))
  //       .filter((constituency) => constituency.polling_stations.length > 0) || []
  //
  //   if (!filteredResults || filteredResults.length < 1) return 0
  //
  //   const allPollingStations = filteredResults.flatMap(
  //     (constituency) => constituency.polling_stations
  //   )
  //
  //   const percentage = allPollingStations[0].percentage
  //   return {
  //     net: 0,
  //     percentage: (!percentage ? 0 : percentage) * 100
  //   }
  // }

  function getSelectedPollingStations() {
    return mParliamentaryMapStore.selectedPollingStations.length < 1
      ? mParliamentaryMapStore.filteredPollingStations
      : mParliamentaryMapStore.selectedPollingStations
  }

  function aggregateStations(): AggregatedData {
    const initialAgeGroup: VotersAgeGroup = {
      '18_30': 0,
      '31_40': 0,
      '41_50': 0,
      '51_60': 0,
      '61_100': 0,
      '101plus': 0
    }

    // Apply filters if provided
    let filteredStations = [...mSelectedVoter.value!.voters]

    const stationCodes = getSelectedPollingStations().map((s) => s.code)

    if (stationCodes && stationCodes.length > 0) {
      filteredStations = filteredStations.filter((station) =>
        stationCodes.includes(station.stationCode)
      )
    }

    const result = filteredStations.reduce(
      (acc, station) => {
        acc.totalStations += 1
        acc.totalVoters += station.totalVoters
        acc.totalVotersMale += station.totalVotersMale
        acc.totalVotersFemale += station.totalVotersFemale

        // Aggregate age groups
        ;(Object.keys(acc.voters) as Array<keyof VotersAgeGroup>).forEach((ageGroup) => {
          acc.voters[ageGroup] += station.voters[ageGroup]
          acc.votersMale[ageGroup] += station.votersMale[ageGroup]
          acc.votersFemale[ageGroup] += station.votersFemale[ageGroup]
        })

        return acc
      },
      {
        totalStations: 0,
        totalVoters: 0,
        totalVotersMale: 0,
        totalVotersFemale: 0,
        voters: { ...initialAgeGroup },
        votersMale: { ...initialAgeGroup },
        votersFemale: { ...initialAgeGroup },
        filteredStations: [] // Initialize empty array
      }
    )

    return result
  }

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(2)}%`
  }

  const getConstituencyVoters = async (constituency: string) => {
    mIsLoading.value = true

    const result = await voterService.getConstituencyVoters(constituency)

    result.fold(
      (value) => {
        mIsLoading.value = false
        mSelectedVoter.value = value as ConstituencyVoterModel
        console.debug('getConstituencyVoters', mSelectedVoter.value)
      },
      (error) => console.log(error)
    )
  }

  const getVoterCounts = async () => {
    mIsLoading.value = true

    const result = await voterService.getVoterCountsConstituency('akwatia')

    result.fold(
      (value) => {
        mIsLoading.value = false
        mVoterCounts.value = value as ConstituencyRegisteredVoter[]
      },
      (error) => console.log(error)
    )
  }

  const getRegionRegisteredVoters = async (region: string) => {
    const result = await voterService.getRegionRegisteredVoters(region)

    result.fold(
      (value) => {
        mConstituencyRegisteredVoter.value = value as ConstituencyRegisteredVoter[]
      },
      (error) => console.log(error)
    )
  }

  return {
    ageGroups: mAgeGroups,
    selectedVoter: mSelectedVoter,
    formatAgeGroup,
    getVotersData,
    getConstituencyVoters,
    getVoterCounts,
    getRegionRegisteredVoters,
    getVotersRegion,
    getVotersConstituency,
    getVotersPollingStation,
    getRegisteredVotersPollingStation,
    aggregateStations,
    resetStore
  }
})
